import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';

import { IcDocument, IcImage, IcNote, IcVideoRecorder } from '@/assets/images';
import { FilePreviewModal, NoDataError } from '@/components';
import { Card, CardContent, Label } from '@/components/TailwindComponents';
import { useFetchSasToken } from '@/hooks';
import { IACaseType, IACaseStatus, IFile, IIACase } from '@/models';
import {
  formatPersonName,
  getFileType,
  getFileUrlWithValidToken,
} from '@/utils';

const caseTypeLabels: Record<string, string> = {
  [IACaseType.USE_OF_FORCE]: 'Use of Force',
  [IACaseType.OFFICER_MISCONDUCT]: 'Officer Misconduct',
  [IACaseType.CITIZEN_COMPLAINT]: 'Citizen Complaint',
  [IACaseType.INTERNAL_COMPLAINT]: 'Internal Complaint',
  [IACaseType.POLICY_VIOLATION]: 'Policy Violation',
};

const statusLabels: Record<string, string> = {
  [IACaseStatus.OPEN]: 'Open',
  [IACaseStatus.IN_PROGRESS]: 'In Progress',
  [IACaseStatus.CLOSED]: 'Closed',
};

const InfoField = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col mb-3">
    <Label htmlFor={label} className="text-[#3E5A6A] text-xs mb-2">
      {label}
    </Label>
    <Label htmlFor={value} className="text-sm font-medium">
      {value || '-'}
    </Label>
  </div>
);

const FileIcon = ({ mimeType }: { mimeType?: string }) => {
  const fileType = getFileType(mimeType);
  if (fileType === 'image') return <IcImage />;
  if (fileType === 'video') return <IcVideoRecorder />;
  return <IcNote />;
};

export const InternalAffairsView = () => {
  const { watch } = useFormContext();
  const { caseData } = useOutletContext<{ caseData: IIACase | null }>();
  const [selectedFile, setSelectedFile] = useState<IFile | undefined>(
    undefined,
  );

  const formValues = watch();

  const assignedOfficerNames = (caseData?.assignedOfficers || [])
    .map((o) => {
      if (typeof o === 'string') return o;
      const name = formatPersonName(o);
      return name || o.email || o._id;
    })
    .filter(Boolean)
    .join(', ');

  const documents: IFile[] = formValues.files || [];
  const sasToken = useFetchSasToken(documents);

  const renderFilePreview = (file: IFile) => {
    const fileType = getFileType(file.mimeType);

    if (fileType === 'image' && file.url && sasToken) {
      const fileUrl = getFileUrlWithValidToken(file, sasToken);
      return (
        <img
          src={fileUrl}
          alt={file.name}
          loading="lazy"
          className="object-contain w-full h-full rounded"
        />
      );
    }

    if (fileType === 'video') {
      return <IcVideoRecorder width={48} />;
    }

    return <IcDocument />;
  };

  return (
    <div
      className="h-screen mt-6 overflow-auto scrollbar-hidden"
      style={{
        overflow: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      {/* General Information Section */}
      <Card className="!border-0 p-5 mb-4 rounded-lg max-w-[708px]">
        <h6 className="mb-4 text-base font-semibold">General information</h6>

        <div className="w-full mb-4 border-t border-grey-20"></div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="col-span-1 sm:col-span-1">
            <InfoField
              label="Case type"
              value={caseTypeLabels[formValues.caseType] || formValues.caseType}
            />
          </div>
          <div className="w-full sm:w-1/2">
            <InfoField
              label="Status"
              value={statusLabels[formValues.status] || formValues.status}
            />
          </div>
        </div>

        <InfoField label="Case details" value={formValues.caseDetails} />

        <InfoField
          label="Investigation notes"
          value={formValues.investigationNotes}
        />

        <InfoField label="Officer details" value={formValues.officerDetails} />

        <InfoField
          label="Findings and recommendations"
          value={formValues.findingsRecommendations}
        />

        <InfoField label="Assigned users" value={assignedOfficerNames || '-'} />
      </Card>

      {/* Media and Documents Section */}
      <Card className="!border-0 p-5 mb-4 rounded-lg max-w-[708px]">
        <h6 className="mb-4 text-base font-semibold">Media and documents</h6>

        <div className="w-full mb-4 border-t border-grey-20"></div>

        {documents.length === 0 ? (
          <NoDataError />
        ) : (
          <div className="grid items-stretch grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            {documents.map((doc: IFile, index: number) => (
              <div className="col-span-1" key={doc._id || index}>
                <Card
                  className="rounded-sm bg-[#E7EAEC80] shadow-none cursor-pointer h-full"
                  onClick={() => setSelectedFile(doc)}
                >
                  <CardContent className="flex flex-col h-full p-2 last:pb-2">
                    {/* Header with icon and filename */}
                    <div className="flex items-center gap-1 mb-2">
                      <FileIcon mimeType={doc.mimeType} />
                      <p className="overflow-hidden text-sm font-medium text-grey-100 text-ellipsis whitespace-nowrap">
                        {doc.name}
                      </p>
                    </div>

                    {/* File preview area */}
                    <div className="flex items-center justify-center h-[200px] rounded bg-white">
                      {renderFilePreview(doc)}
                    </div>

                    {/* Description */}
                    <p className="text-grey-100 text-xs mt-2 truncate min-h-[18px]">
                      {doc.description || '\u00A0'}
                    </p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        )}
      </Card>

      <FilePreviewModal
        open={Boolean(selectedFile)}
        file={selectedFile}
        onClose={() => setSelectedFile(undefined)}
        sasToken={sasToken}
      />
    </div>
  );
};
